import { createFormatForDescription as cffd } from '../createFormatForDescription'
import Description from '../description'
import {
  AffectedNode,
  CreatedNode as CreatedNodeType,
  DeletedNode as DeletedNodeType,
  ModifiedNode as ModifiedNodeType,
} from '../types/Metadata'
import { numberToRankString } from '../util'

import { Explanation, explain, explainOptional } from './Explanation'

// TODO: get a better explanation on the fields?
// Maybe by going through https://xrpl.org/ledger-object-types.html ?

/**
 * Stores the different kinds of affected nodes.
 *
 * @enum {number}
 */
enum AffectedNodeType {
  CreatedNode = 'CreatedNode',
  DeletedNode = 'DeletedNode',
  ModifiedNode = 'ModifiedNode',
}

/**
 * Base type for explanations on affected nodes.
 *
 * @property type - How the node was changed (either created, deleted, or modified).
 * @property ledgerEntryType - An explanation on the ledgerEntryType.
 * @property ledgerIndex - An explanation on the ledgerIndex.
 */
interface AffectedNodeBaseExplanation {
  readonly type: AffectedNodeType
  readonly ledgerEntryType: Explanation<string>
  readonly ledgerIndex: Explanation<string>
}

/**
 * Explains a created node.
 *
 * @extends AffectedNodeBaseExplanation
 *
 * @property newFields - An explanation of the new fields of the node.
 */
interface CreatedNodeExplanation extends AffectedNodeBaseExplanation {
  readonly type: AffectedNodeType.CreatedNode
  readonly newFields: Explanation<{ [key: string]: unknown }>
}

/**
 * Explains a deleted node.
 *
 * @extends AffectedNodeBaseExplanation
 *
 * @property finalFields - The fields of the node prior to deletion.
 */
interface DeletedNodeExplanation extends AffectedNodeBaseExplanation {
  readonly type: AffectedNodeType.DeletedNode
  readonly finalFields: Explanation<{ [key: string]: unknown }>
}

/**
 * Explains a modified node.
 *
 * @extends AffectedNodeBaseExplanation
 *
 * @property modifiedFields - The fields of the node that the transaction modified.
 * @property newFields - The fields of the node that the transaction created.
 * @property previousTxnID - The previous transaction to change this node.
 * @property previousTxnLgrSeq - The ledger containing the previous transaction to change this node.
 */
interface ModifiedNodeExplanation extends AffectedNodeBaseExplanation {
  readonly type: AffectedNodeType.ModifiedNode
  readonly modifiedFields: Explanation<{
    [key: string]: { old: unknown; new: unknown; description: Description }
  }>
  readonly newFields: Explanation<{
    [key: string]: { new: unknown; description: Description }
  }>
  readonly previousTxnID?: Explanation<string>
  readonly previousTxnLgrSeq?: Explanation<number>
}

/**
 * Combined type for all possible node explanations.
 *
 */
type AffectedNodeExplanation =
  | CreatedNodeExplanation
  | DeletedNodeExplanation
  | ModifiedNodeExplanation

export { AffectedNodeExplanation, AffectedNodeType }

/**
 * Creates an explanation on a created node.
 *
 * @param node - The node to be explained.
 * @returns The explanation of the node.
 */
function createCreatedNodeExplanation(
  node: CreatedNodeType,
): CreatedNodeExplanation {
  const ledgerEntryType = explain(
    node.LedgerEntryType,
    `A ${cffd(node.LedgerEntryType, 'ledgerEntryType')} node was created.`,
  )

  const ledgerIndex = explain(
    node.LedgerIndex,
    `The node has ID ${cffd(
      node.LedgerIndex,
      'ledgerIndex',
    )} within the ledger.`,
  )

  const newFields = explain(node.NewFields, "These are the new node's fields:")

  return {
    type: AffectedNodeType.CreatedNode,
    ledgerEntryType,
    ledgerIndex,
    newFields,
  }
}

/**
 * Creates an explanation on a deleted node.
 *
 * @param node - The node to be explained.
 * @returns The explanation of the node.
 */
function createDeletedNodeExplanation(
  node: DeletedNodeType,
): DeletedNodeExplanation {
  const ledgerEntryType = explain(
    node.LedgerEntryType,
    `A ${cffd(node.LedgerEntryType, 'ledgerEntryType')} node was deleted.`,
  )

  const ledgerIndex = explain(
    node.LedgerIndex,
    `The node had ID ${cffd(
      node.LedgerIndex,
      'ledgerIndex',
    )} within the ledger.`,
  )

  const fields = node.FinalFields ?? node.PreviousFields ?? {}

  const finalFields = explain(
    fields,
    "These were the node's fields prior to deletion:",
  )

  return {
    type: AffectedNodeType.DeletedNode,
    ledgerEntryType,
    ledgerIndex,
    finalFields,
  }
}

/**
 * Create explanation for the fields of a modified node.
 *
 * @param node - The node to be explained.
 * @returns Two dictionaries containing the modified fields and the new fields of the node.
 */
function newAndModifiedValues(
  node: ModifiedNodeType,
): {
  modifiedFieldValues: {
    [key: string]: { old: unknown; new: unknown; description: Description }
  }
  newFieldValues: {
    [key: string]: { new: unknown; description: Description }
  }
} {
  const modifiedFieldValues: {
    [key: string]: { old: unknown; new: unknown; description: Description }
  } = {}

  const newFieldValues: {
    [key: string]: { new: unknown; description: Description }
  } = {}

  const prevFields = node.PreviousFields ?? {}
  const finalFields = node.FinalFields ?? {}

  Object.entries(finalFields).forEach(([key, value]) => {
    if (key in prevFields) {
      const old = prevFields[key]
      modifiedFieldValues[key] = {
        old,
        new: value,
        description: new Description(
          `${cffd(key, 'key')} was changed from ${cffd(
            JSON.stringify(old),
            'old-value',
          )} to ${cffd(JSON.stringify(value), 'new-value')}`,
        ),
      }
    } else {
      newFieldValues[key] = {
        new: value,
        description: new Description(
          `${cffd(key, 'key')} was set to ${cffd(
            JSON.stringify(value),
            'new-value',
          )}`,
        ),
      }
    }
  })

  return { modifiedFieldValues, newFieldValues }
}

/**
 * Creates an explanation on a modified node.
 *
 * @param node - The node to be explained.
 * @returns The explanation of the node.
 */
function createModifiedNodeExplanation(
  node: ModifiedNodeType,
): ModifiedNodeExplanation {
  const ledgerEntryType = explain(
    node.LedgerEntryType,
    `A ${cffd(node.LedgerEntryType, 'ledgerEntryType')} node was modified.`,
  )

  const ledgerIndex = explain(
    node.LedgerIndex,
    `The node has ID ${cffd(
      node.LedgerIndex,
      'ledgerIndex',
    )} within the ledger.`,
  )

  const { modifiedFieldValues, newFieldValues } = newAndModifiedValues(node)

  const modifiedFields = explain(
    modifiedFieldValues,
    'These are the fields changed by the transaction:',
  )

  const newFields = explain(
    newFieldValues,
    'These are the new fields created by the transaction:',
  )

  const previousTxnID = explainOptional(
    node.PreviousTxnID,
    (txnId) =>
      `The last transaction to modify this node had hash ${cffd(
        txnId,
        'previousTxnID',
      )}`,
  )

  const previousTxnLgrSeq = explainOptional(
    node.PreviousTxnLgrSeq,
    (legSeq) =>
      `This node was last modified by a transaction in the ${cffd(
        numberToRankString(legSeq),
        'previousTxnLgrSeq',
      )} ledger.`,
  )

  return {
    type: AffectedNodeType.ModifiedNode,
    ledgerEntryType,
    ledgerIndex,
    modifiedFields,
    newFields,
    ...(previousTxnID && { previousTxnID }),
    ...(previousTxnLgrSeq && { previousTxnLgrSeq }),
  }
}

/**
 * Creates an explanation on any type of affected node.
 *
 * @throws Throws an error if the node doesn't a CreatedNode, DeletedNode, and ModifiedNode property.
 *
 * @param node - The node to be explained.
 * @returns The explanation of the node.
 */
export function createAffectedNodeExplanation(
  node: AffectedNode,
): AffectedNodeExplanation {
  if (node.CreatedNode) {
    return createCreatedNodeExplanation(node.CreatedNode)
  }
  if (node.DeletedNode) {
    return createDeletedNodeExplanation(node.DeletedNode)
  }
  if (node.ModifiedNode) {
    return createModifiedNodeExplanation(node.ModifiedNode)
  }
  throw Error(
    'Affected node did not have a CreatedNode, DeletedNode, or ModifiedNode property',
  )
}
